import {
  Form,
  Modal,
  Input,
  DatePicker,
  TimePicker,
  Select,
  Alert,
  Switch,
  Typography,
  Radio,
  Checkbox,
  Button,
  InputNumber,
  Space,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useContext, useEffect, useMemo, useState } from "react";
import { CompanyContext } from "../../../contexts/company-context";
import { EmployeeOperation } from "../../../serviceoperation/EmployeeOperation";
import { TimeZoneOperation } from "../../../serviceoperation/TimezoneOperation";
import { EmployeeType, ScheduleType } from "../components/week/week-types";
import { TimeZoneType } from "./timeZone-types";
import dayjs, { Dayjs } from "dayjs";
import { ScheduleOperation } from "../../../serviceoperation/ScheduleOperation";
import { BranchOperation } from "../../../serviceoperation/BranchOperation";
import { LocationType } from "./location-types";
import { TimeOffOperation } from "../../../serviceoperation/TimeoffOperation";
import { ScheduleTemplate } from "../../../types/scheduleTemplate";
import { TaskTemplate } from "../../../types/taskTemplate";
import { AmPMFormat, ConvertTo12hr } from "../../../Util/dateutil";
import { ScheduleTempOperation } from "../../../serviceoperation/ScheduleTempOperation";
import { TaskTemplateOperation } from "../../../serviceoperation/TaskTemplateOperation";

interface Values {
  employee: string;
  title: string;
  scheduleDate: Dayjs;
  scheduleTime: Dayjs[];
  note: string;
  modifier: string;
  timeZone: string;
  location: string;
  notify: string;
  customTasks?: {
    name: string;
    description?: string;
    category?: string;
    estimatedMinutes?: number;
    requirePhoto?: boolean;
    requireNote?: boolean;
    sortOrder?: number;
  }[];
}

interface AddScheduleEventData extends Partial<ScheduleType> {
  employeeId?: string;
  date?: string | Date | Dayjs;
}

interface AddScheduleFormProps {
  eventData?: AddScheduleEventData;
  employee?: EmployeeType[];
  open: boolean;
  onCreate: (values: Values) => void;
  onCancel: () => void;
}

//AddScheduleForm
const AddScheduleForm: React.FC<AddScheduleFormProps> = ({
  eventData,
  employee,
  open,
  onCreate,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const { Text } = Typography;

  const { selectedCompany } = useContext(CompanyContext);
  const format = "hh:mm A";
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showWarning, setWarning] = useState(false);
  // Get TimeZone...
  const [timeZones, setTimeZone] = useState<TimeZoneType[]>([]);
  // List of employee for a given logged in user.
  // This will contain a data regardless of who is logged in
  // as a logged in user will be in this list.
  // Get Corporation Locations...
  const [location, setLocation] = useState<LocationType[]>([]);
  const timeZoneOps = new TimeZoneOperation();
  const [userTimeOffError, setUserTimeOffError] = useState<boolean>(false);

  const [selectedEmployee, setSelectedEmployee] = useState<React.Key>();
  const [selectedDateTime, setSelectedDateTime] = useState<Date>();
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();

  const [usePreset, setUsePreset] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );

  const [templates, setTemplates] = useState<ScheduleTemplate[]>();
  const tempOps = new ScheduleTempOperation();

  const [taskTemplates, setTaskTemplates] = useState<TaskTemplate[]>();
  const taskTempOps = new TaskTemplateOperation();

  const [isDraft, setIsDraft] = useState(false);
  const [selectedTaskTemplateIds, setSelectedTaskTemplateIds] = useState<
    string[]
  >([]);

  // Handle draft change and update notification setting
  const handleDraftChange = (checked: boolean) => {
    setIsDraft(checked);
    if (checked) {
      // If draft is checked, set notify to "private" (No)
      form.setFieldsValue({ notify: "private" });
    }
  };

  useEffect(() => {
    tempOps
      .getScheduleTempAsync(selectedCompany?.corporationId as number, 1, 255)
      .then((value) => {
        setTemplates(
          value.results.map((x) => ({
            id: x.templateId.toString(),
            name: x.name,
            description: x.name,
            startWorkHour: x.startHour,
            startWorkMin: x.startMinute,
            endWorkHour: x.endHour,
            endWorkMin: x.endMinute,
            isDefault: false,
            createdAt: x.createdDateTimeUTC,
            updatedAt: x.createdDateTimeUTC,
          })),
        );

        if (value.rowCount === 0) setUsePreset(false);
      });

    taskTempOps
      .getTaskTemplateAsync(selectedCompany?.corporationId as number, 1, 255)
      .then((value) => {
        setTaskTemplates(
          value.results
            .filter((x) => x.activeInd)
            .map((x) => ({
              id: x.taskTemplateId.toString(),
              name: x.name,
              description: x.description,
              category: x.category,
              branchId: x.branchId,
              items: x.items || [],
              isDefault: x.isDefault,
              isActive: x.activeInd ?? false,
              createdAt: x.createdDateTimeUTC,
              updatedAt: x.createdDateTimeUTC,
            })),
        );
      });
  }, [selectedCompany]);

  const handleTemplateChange = (value: string) => {
    setSelectedTemplateId(value);
    const template = templates?.find((t) => t.id === value);
    if (template) {
      form.setFieldsValue({
        scheduleTime: [
          dayjs().hour(template.startWorkHour).minute(template.startWorkMin),
          dayjs().hour(template.endWorkHour).minute(template.endWorkMin),
        ],
      });
    }
  };

  //This was the old way of getting the EmpData.
  // If we have to revert the change, then we can easily just uncomment
  // and use this code with state changes.
  /*useEffect(() => {
    const empId = userInfo?.employeeCorporation
      ?.filter((x) => x.corporationId === selectedCompany?.corporationId)
      .map((x) => x.employeeId)
      .at(0);
    const fetchEmployees = async () => {
      const employeeOps = new EmployeeOperation();
      const response = await employeeOps.getEmployeeAsync(
        selectedCompany?.corporationId as number,
        1,
        255
      );
      setEmployeez(
        response.results
          .filter((x) => x.employeeId !== empId && x.isActive)
          .map((s) => ({
            key: s.employeeId,
            name: `${s.firstName} ${s.lastName}`,
          }))
      );
    };

    fetchEmployees();
  }, [selectedCompany]);*/

  useMemo(() => {
    timeZoneOps.getTimeZoneAsync().then((value) => {
      setTimeZone(
        value.map((s) => ({
          key: s.id,
          name: s.id,
        })),
      );
    });
  }, []);

  const insertSchedule = async (values: Values): Promise<boolean> => {
    let startHour: number | null = null;
    let endHour: number | null = null;
    let startMin: number | null = null;
    let endMin: number | null = null;

    if (usePreset && selectedTemplateId) {
      const selected = templates?.find((t) => t.id === selectedTemplateId);
      if (selected) {
        startHour = selected.startWorkHour;
        startMin = selected.startWorkMin;
        endHour = selected.endWorkHour;
        endMin = selected.endWorkMin;
      }
    } else if (values.scheduleTime) {
      startHour = dayjs(values.scheduleTime[0]).hour();
      startMin = dayjs(values.scheduleTime[0]).minute();
      endHour = dayjs(values.scheduleTime[1]).hour();
      endMin = dayjs(values.scheduleTime[1]).minute();
    }

    const scheduleOps = new ScheduleOperation();
    let success = false;
    const scheduleDate = new Date(values.scheduleDate.toDate());
    scheduleDate.setHours(0, 0, 0, 0);

    if (
      startHour === null ||
      startMin === null ||
      endHour === null ||
      endMin === null
    ) {
      return false;
    }

    try {
      const customTasks = (values.customTasks ?? [])
        .map((task, index) => ({
          name: (task.name ?? "").trim(),
          description: task.description,
          category: task.category,
          estimatedMinutes: Number(task.estimatedMinutes ?? 0),
          requirePhoto: !!task.requirePhoto,
          requireNote: !!task.requireNote,
          sortOrder: Number(task.sortOrder ?? index),
        }))
        .filter((task) => task.name.length > 0);

      await scheduleOps
        .postScheduleAsync(
          selectedCompany?.corporationId as number,
          values.employee,
          {
            title: values.title,
            note: values.note,
            workDay: scheduleDate,
            startWorkHour: startHour,
            startWorkMinute: startMin,
            endWorkHour: endHour,
            endWorkMinute: endMin,
            timeZoneId: values.timeZone,
            branchId: values.location,
            isDraft: isDraft,
            taskTemplateId: selectedTaskTemplateIds[0],
            taskTemplateIds:
              selectedTaskTemplateIds.length > 0
                ? selectedTaskTemplateIds
                : undefined,
            customTasks: customTasks.length > 0 ? customTasks : undefined,
            notification: {
              notifyEmployee: values.notify === "public" ? true : false,
              title: "New Schedule Posted!",
              body: `Scheduled posted for: ${scheduleDate.toLocaleDateString()}`,
            },
          },
        )
        .then((x) => {
          success =
            x.statusCode != undefined && x.statusCode.toString() === "201";
        });
    } catch (e) {}

    return success;
  };

  useEffect(() => {
    const branchOps = new BranchOperation();
    branchOps
      .getBranchAsync(selectedCompany?.corporationId as number)
      .then((value) => {
        if (value.data) {
          setLocation(
            value.data.map((s) => ({
              key: s.branchID,
              name: s.name,
              color: s.color,
            })),
          );
        }
      });
  }, []);

  // On Change of Department selection.
  const handleSelectedDateChange = (selectedDate: Dayjs | null) => {
    setSelectedDateTime(selectedDate ? selectedDate.toDate() : undefined);
  };

  const templateOptions = templates?.map((template) => {
    const start = `${String(template.startWorkHour).padStart(2, "0")}:${String(
      template.startWorkMin,
    ).padStart(2, "0")}`;
    const end = `${String(template.endWorkHour).padStart(2, "0")}:${String(
      template.endWorkMin,
    ).padStart(2, "0")}`;

    return {
      value: template.id,
      label: (
        <div>
          <Text strong>{template.name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {start} - {end}
          </Text>
        </div>
      ),
    };
  });

  // UserEffect call to Make sure Employee is not set to Off on this day.
  useEffect(() => {
    if (selectedEmployee && selectedDateTime) {
      setUserTimeOffError(false);
      setShowError(false);
      setWarning(false);

      const timeOffOps = new TimeOffOperation();
      timeOffOps
        .getScheduleTimeOffAsync(selectedCompany?.corporationId as number, {
          includeDirectReport: true,
          weekOf: new Date().toDateString(),
          employeeId: selectedEmployee as string,
          page: 1,
          pageSize: 25,
        })
        .then((value) => {
          const currDate = new Date();
          currDate.setHours(0, 0, 0, 0);
          const newVal = value.results.filter(
            (x) =>
              x.employee.employeeId == selectedEmployee &&
              x.timeOffStatus != "Cancelled" &&
              new Date(x.startDate) <= selectedDateTime &&
              new Date(x.endDate) >= selectedDateTime,
          );
          if (newVal.length > 0) {
            setUserTimeOffError(true);
            setShowError(true);
          }
        });

      const empOps = new EmployeeOperation();
      empOps
        .getEmployeeAvailabilityAsync(
          selectedCompany?.corporationId as number,
          selectedEmployee as string,
        )
        .then((availability) => {
          setWarning(
            availability.data?.some(
              (x) =>
                x.dayIndex === new Date(selectedDateTime).getDay() &&
                !x.available,
            ) ?? false,
          );
        });
    }
  }, [selectedEmployee, selectedDateTime]);

  return (
    <Modal
      open={open}
      title="Add new Schedule"
      okText="Create"
      cancelText="Cancel"
      confirmLoading={confirmLoading}
      onCancel={onCancel}
      onOk={() => {
        // Before we test the form, we need to
        // make sure selected Employee does not have
        // TimeOff Request.
        if (!userTimeOffError) {
          setConfirmLoading(true);
          form
            // Validate the required fields.
            .validateFields()
            .then(async (values) => {
              // Insert Employee Schedule..
              // This is where you insert Schedule
              // and then return to Parent Component. :)
              if (await insertSchedule(values)) {
                form.resetFields();
                onCreate(values);
              } else {
                setShowError(true);
                // Stay on the Page and keep the Modal open and display Error message.
              }
              setConfirmLoading(false);
            })
            .catch((info) => {
              console.log("Validate Failed:", info);
              setConfirmLoading(false);
            });
        }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        name="form_in_modal"
        initialValues={{ modifier: "public" }}
      >
        <Form.Item
          name="employee"
          hasFeedback
          label="Employee"
          initialValue={eventData?.employeeId}
          rules={[
            {
              required: true,
              message: "Please select an employee!",
            },
          ]}
          style={{ marginTop: 24 }}
        >
          <Select
            showSearch
            onSelect={setSelectedEmployee}
            placeholder="Select Employee"
            allowClear
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={(employee ?? []).map((emp) => {
              return {
                value: emp.key,
                label: emp.name,
              };
            })}
          />
        </Form.Item>
        <Form.Item
          name="title"
          label="Title"
          rules={[
            {
              required: true,
              message: "Please input the title of schedule!",
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item label="Schedule Date Time" style={{ marginBottom: 12 }}>
          {/* Date Picker */}
          <Form.Item
            name="scheduleDate"
            initialValue={eventData ? dayjs(eventData?.date) : null}
            rules={[{ required: true, message: "Please select date" }]}
            style={{
              display: "inline-block",
              width: "calc(33% - 8px)",
              margin: "0 4px",
            }}
          >
            <DatePicker
              onSelect={handleSelectedDateChange}
              format="YYYY-MM-DD"
            />
          </Form.Item>

          {/* Preset vs Manual Toggle */}
          <Form.Item
            style={{
              display: "inline-block",
              width: "calc(20% - 8px)",
              margin: "0 4px",
            }}
          >
            <Switch
              checkedChildren="Preset"
              unCheckedChildren="Manual"
              checked={usePreset}
              onChange={(checked) => setUsePreset(checked)}
            />
          </Form.Item>

          {/* Preset Dropdown */}
          {usePreset && (
            <Form.Item
              name="selectedTemplateId"
              rules={[{ required: true, message: "Please select a preset" }]}
              style={{
                display: "inline-block",
                width: "calc(47% - 8px)",
                margin: "0 4px",
              }}
            >
              <Select
                showSearch
                placeholder="Select a preset"
                allowClear
                optionFilterProp="children"
                options={templateOptions}
                onChange={handleTemplateChange}
                value={selectedTemplateId}
              />

              {usePreset && selectedTemplateId && (
                <div style={{ marginTop: 8 }}>
                  {(() => {
                    const selected = templates?.find(
                      (t) => t.id === selectedTemplateId,
                    );
                    if (!selected) return null;

                    const start = `${ConvertTo12hr(selected.startWorkHour)}:${
                      selected.startWorkHour === 0
                        ? "00"
                        : selected.startWorkMin
                    } ${AmPMFormat(selected.startWorkHour)}`;
                    const end = `${ConvertTo12hr(selected.endWorkHour)}:${
                      selected.endWorkHour === 0 ? "00" : selected.startWorkMin
                    } ${AmPMFormat(selected.endWorkHour)}`;

                    //`${ConvertTo12hr(selected.startWorkHour)}:${selected.startWorkMinute === 0 ? "00" : selected.startWorkMinute} ${AmPMFormat(selected.startWorkHour)}`

                    return (
                      <Text type="secondary">
                        Selected time range:{" "}
                        <Text strong>
                          {start} - {end}
                        </Text>
                      </Text>
                    );
                  })()}
                </div>
              )}
            </Form.Item>
          )}

          {/* Manual TimePicker */}
          {!usePreset && (
            <Form.Item
              name="scheduleTime"
              preserve={false}
              rules={[{ required: true, message: "Please select time" }]}
              style={{
                display: "inline-block",
                width: "calc(47% - 8px)",
                margin: "0 4px",
              }}
            >
              <TimePicker.RangePicker format={format} minuteStep={5} />
            </Form.Item>
          )}
        </Form.Item>

        {/*<Form.Item label="Schedule Date Time" style={{ marginBottom: 12 }}>
          <Form.Item
            name="scheduleDate"
            rules={[
              {
                required: true,
                message: "Please select date",
              },
            ]}
            style={{
              display: "inline-block",
              width: "calc(33% - 8px)",
              margin: "0 4px",
            }}
          >
            <DatePicker
              onSelect={handleSelectedDateChange}
              format="YYYY-MM-DD"
            />
          </Form.Item>
          <Form.Item
            name="scheduleTime"
            {...rangeConfig}
            style={{
              display: "inline-block",
              //width: "calc(33% - 8px)",
              margin: "0 4px",
            }}
          >
            <TimePicker.RangePicker minuteStep={5} format={format} />
          </Form.Item>
        </Form.Item>*/}
        <Form.Item
          name="timeZone"
          rules={[
            {
              required: true,
              message: "Please select a timezone!",
            },
          ]}
          initialValue={timeZone}
        >
          <Select
            placeholder="Select TimeZone"
            defaultValue={timeZone}
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={timeZones.map((tmz) => {
              return {
                value: tmz.key,
                label: tmz.name,
              };
            })}
          />
        </Form.Item>
        <Form.Item name="location" label="Location">
          <Select
            placeholder="Select Location"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={location.map((loc) => {
              return {
                value: loc.key,
                label: loc.name,
              };
            })}
          />
        </Form.Item>
        <Form.Item name="note" label="Notes">
          <Input.TextArea showCount maxLength={255} />
        </Form.Item>
        <Form.Item name="taskTemplate" label="Task Template (Optional)">
          <Select
            mode="multiple"
            placeholder="Select Task Template"
            allowClear
            showSearch
            optionFilterProp="children"
            value={selectedTaskTemplateIds}
            onChange={(value) => setSelectedTaskTemplateIds(value || [])}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={taskTemplates?.map((task) => ({
              value: task.id,
              label: task.name,
            }))}
          />
        </Form.Item>

        <Form.List name="customTasks">
          {(fields, { add, remove }) => (
            <Space direction="vertical" style={{ width: "100%" }} size={8}>
              <Text strong>Add Custom Tasks (Optional)</Text>

              {fields.map((field, index) => (
                <div
                  key={field.key}
                  style={{
                    border: "1px solid #d9d9d9",
                    borderRadius: 6,
                    padding: 12,
                  }}
                >
                  <Space
                    style={{ width: "100%", justifyContent: "space-between" }}
                    align="center"
                  >
                    <Text strong>Task {index + 1}</Text>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => remove(field.name)}
                    />
                  </Space>

                  <Form.Item
                    {...field}
                    name={[field.name, "name"]}
                    label="Name"
                    rules={[
                      { required: true, message: "Please enter task name" },
                    ]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    {...field}
                    name={[field.name, "description"]}
                    label="Description"
                  >
                    <Input.TextArea rows={2} />
                  </Form.Item>

                  <Form.Item
                    {...field}
                    name={[field.name, "category"]}
                    label="Category"
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    {...field}
                    name={[field.name, "estimatedMinutes"]}
                    label="Estimated Minutes"
                  >
                    <InputNumber min={0} style={{ width: "100%" }} />
                  </Form.Item>

                  <Form.Item
                    {...field}
                    name={[field.name, "sortOrder"]}
                    label="Sort Order"
                    initialValue={index}
                  >
                    <InputNumber min={0} style={{ width: "100%" }} />
                  </Form.Item>

                  <Space>
                    <Form.Item
                      {...field}
                      name={[field.name, "requirePhoto"]}
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Checkbox>Require Photo</Checkbox>
                    </Form.Item>

                    <Form.Item
                      {...field}
                      name={[field.name, "requireNote"]}
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Checkbox>Require Note</Checkbox>
                    </Form.Item>
                  </Space>
                </div>
              ))}

              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() =>
                  add({
                    estimatedMinutes: 0,
                    requirePhoto: false,
                    requireNote: false,
                    sortOrder: fields.length,
                  })
                }
              >
                Add Custom Task
              </Button>
            </Space>
          )}
        </Form.List>
        <Form.Item name="isDraft" label="Schedule Status">
          <Checkbox
            checked={isDraft}
            onChange={(e) => handleDraftChange(e.target.checked)}
          >
            Save as Draft
          </Checkbox>
        </Form.Item>
        <Form.Item
          name="notify"
          label="Notify Employee"
          className="collection-create-form_last-form-item"
          tooltip={
            isDraft ? "Notifications are disabled for draft schedules" : ""
          }
        >
          <Radio.Group disabled={isDraft}>
            <Radio value="public">Yes</Radio>
            <Radio value="private">No</Radio>
          </Radio.Group>
        </Form.Item>
      </Form>
      {showError ? (
        <Alert
          message={
            !userTimeOffError
              ? "There was an error processing your request. Please try again later."
              : "The selected employee has submitted a time-off request for this period."
          }
          type="error"
          showIcon
        />
      ) : null}
      {showWarning ? (
        <Alert
          message={
            "The chosen employee's preference does not align with the schedule's specifications"
          }
          type="warning"
          showIcon
        />
      ) : null}
    </Modal>
  );
};

export default AddScheduleForm;
